import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { faviconLoader } from '../utils/faviconLoader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LumynBadge } from '../components/common/LumynBadge';
import { EmptyState } from '../components/common/EmptyState';
import { SkeletonCard } from '../components/common/SkeletonCard';
import {
  CheckCircle2, XCircle, RefreshCw, Shield, Sparkles, ChevronLeft, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const FLAG_COLORS = {
  ambiguous: 'bg-amber-500/20 text-amber-700',
  factual_mismatch: 'bg-red-500/20 text-red-700',
  source_unanchored: 'bg-orange-500/20 text-orange-700',
  biased: 'bg-purple-500/20 text-purple-700',
  audit_failed: 'bg-muted-foreground/20 text-muted-foreground',
};

function QuestionCard({ q, onApprove, onReject, onRegenerate, onAudit, busy }) {
  const correctTexts = (q.options || []).filter(o => o.is_correct).map(o => o.text);
  const audit = q.ai_audit;

  return (
    <Card className="overflow-hidden" data-testid={`pending-q-${q.question_id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2">
          <span className="text-xs text-muted-foreground font-mono mt-0.5">#{q.order + 1}</span>
          <CardTitle className="text-sm flex-1">{q.question_text}</CardTitle>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
          <span className="bg-muted px-1.5 py-0.5 rounded">{q.question_type}</span>
          {q.difficulty_estimate && <span className="bg-muted px-1.5 py-0.5 rounded">{q.difficulty_estimate}</span>}
          {q.bloom_level && <span className="bg-muted px-1.5 py-0.5 rounded">{q.bloom_level}</span>}
          {q.language && <span className="bg-muted px-1.5 py-0.5 rounded">{q.language}</span>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-1">
        {q.options?.length > 0 && (
          <div className="space-y-1">
            {q.options.map((o, i) => (
              <div key={o.id || i} className={`text-xs p-2 rounded border ${o.is_correct ? 'bg-green-500/10 border-green-500/40' : 'bg-muted/30'}`}>
                <span className="font-mono mr-2">{String.fromCharCode(65 + i)}.</span>{o.text}
                {o.is_correct && <CheckCircle2 className="inline w-3 h-3 ml-1 text-green-600" />}
              </div>
            ))}
          </div>
        )}
        {q.correct_answers?.length > 0 && q.options?.length === 0 && (
          <div className="text-xs"><strong>Acceptable answers:</strong> {q.correct_answers.join(', ')}</div>
        )}
        {q.explanation && (
          <p className="text-xs text-muted-foreground italic">{q.explanation}</p>
        )}
        {q.source_citation && (
          <div className="text-[11px] bg-warm-200/50 border border-warm-300 rounded p-2">
            <strong>Source:</strong> {q.source_citation.snippet || q.source_citation}
            {q.source_citation.location_hint && <span className="text-muted-foreground"> · {q.source_citation.location_hint}</span>}
          </div>
        )}

        {/* Audit verdict */}
        {audit && (
          <div className={`text-[11px] rounded p-2 border ${audit.verdict === 'pass' ? 'bg-green-500/5 border-green-500/30' : 'bg-amber-500/10 border-amber-500/40'}`}>
            <div className="flex items-center gap-1 mb-0.5">
              <Shield className="w-3 h-3" />
              <strong>Audit:</strong> {audit.verdict}
              {audit.confidence !== undefined && <span className="text-muted-foreground"> · {(audit.confidence * 100).toFixed(0)}% confidence</span>}
            </div>
            {audit.flags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {audit.flags.map(f => <span key={f} className={`px-1.5 py-0.5 rounded text-[10px] ${FLAG_COLORS[f] || 'bg-muted-foreground/20 text-muted-foreground'}`}>{f}</span>)}
              </div>
            )}
            {audit.notes && <p className="text-[10px] mt-1 text-muted-foreground">{audit.notes}</p>}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" onClick={() => onApprove(q.question_id)} disabled={busy} data-testid={`approve-${q.question_id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(q.question_id)} disabled={busy} data-testid={`reject-${q.question_id}`}>
            <XCircle className="w-3.5 h-3.5 mr-1" />Reject
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onRegenerate(q.question_id)} disabled={busy}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" />Regenerate
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onAudit(q.question_id)} disabled={busy}>
            <Shield className="w-3.5 h-3.5 mr-1" />{audit ? 'Re-audit' : 'Audit'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function QuizReviewQueuePage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { get, post } = useApi();

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const back = location.state?.back || '/editor/modules';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await get(`/quizzes/${quizId}/questions/pending`);
      setPending(Array.isArray(items) ? items : []);
    } catch (e) { toast.error('Failed to load review queue'); }
    finally { setLoading(false); }
  }, [get, quizId]);

  useEffect(() => { load(); }, [load]);

  const wrap = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const handleApprove = (qid) => wrap(async () => {
    await post(`/quizzes/${quizId}/questions/${qid}/review`, { action: 'approve' });
    toast.success('Approved');
  });
  const handleReject = (qid) => {
    if (!confirm('Reject and remove this question?')) return;
    wrap(async () => {
      await post(`/quizzes/${quizId}/questions/${qid}/review`, { action: 'reject', reason: 'rejected_by_trainer' });
      toast.success('Rejected');
    });
  };
  const handleRegenerate = (qid) => wrap(async () => {
    const releaseFavicon = faviconLoader.start();
    try {
      await post(`/quizzes/${quizId}/questions/${qid}/regenerate`, {});
      toast.success('Regenerated (still pending)');
    } finally {
      releaseFavicon();
    }
  });
  const handleAudit = (qid) => wrap(async () => {
    await post(`/quizzes/${quizId}/questions/${qid}/audit`, {});
    toast.success('Audit complete');
  });

  const handleApproveAll = async () => {
    if (!confirm(`Approve all ${pending.length} pending questions?`)) return;
    setBusy(true);
    let ok = 0;
    let failed = 0;
    try {
      for (const q of pending) {
        try {
          await post(`/quizzes/${quizId}/questions/${q.question_id}/review`, { action: 'approve' });
          ok += 1;
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) toast.success(`Approved all ${ok} questions`);
      else toast.warning(`Approved ${ok} of ${pending.length} (${failed} failed)`);
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 relative" data-testid="review-queue-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-quiz)]" />
      <div className="relative z-10 space-y-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="space-y-1.5 flex items-start gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(back)} data-testid="review-back-btn" className="mt-1.5">
              <ChevronLeft className="w-4 h-4" />Back
            </Button>
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-quiz)]/10 text-[var(--lumyn-quiz)] text-[11px] font-semibold uppercase tracking-[0.14em]">
                <Sparkles className="w-3.5 h-3.5" />Review queue
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">AI Review Inbox</h1>
              <p className="text-sm text-muted-foreground max-w-prose">
                {pending.length === 0
                  ? 'Inbox zero — every AI-drafted question for this quiz has been reviewed.'
                  : `${pending.length} ${pending.length === 1 ? 'question' : 'questions'} need your call.`}
              </p>
            </div>
          </div>
          {pending.length > 0 && (
            <Button
              onClick={handleApproveAll}
              disabled={busy}
              data-testid="approve-all-btn"
              className="bg-[var(--lumyn-quiz)] hover:bg-[var(--lumyn-quiz)]/90 text-white shadow-md shadow-[var(--lumyn-quiz)]/20"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />Approve all ({pending.length})
            </Button>
          )}
        </div>

        {loading ? (
          <SkeletonCard />
        ) : pending.length === 0 ? (
          <EmptyState icon={CheckCircle2} title="Inbox zero" description="No AI questions pending review for this quiz." />
        ) : (
          <>
            <div className="text-xs text-amber-900 dark:text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
              <span>These questions are HIDDEN from learners until you approve them. Re-audit any flagged ones, or regenerate stems that need rework.</span>
            </div>
            <div className="grid lg:grid-cols-2 gap-3">
              {pending.map(q => (
                <QuestionCard key={q.question_id} q={q} busy={busy}
                  onApprove={handleApprove} onReject={handleReject}
                  onRegenerate={handleRegenerate} onAudit={handleAudit} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
